// Add your custom JS here.
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Import Swiper - using proper module imports
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Register GSAP ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// Use a self-executing function to avoid polluting the global namespace
(function () {
    // Paused hero intro timeline, built in initPage() and played when the
    // curtain reveal completes (null on pages without a hero).
    let heroIntroTl = null;
    // Create a debounce function to limit how often a function can be called
    function debounce(func, wait) {
        let timeout;
        return function () {
            const context = this;
            const args = arguments;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    }

    /**
     * Initialize all page-specific scripts (single native page load).
     */
    function initPage() {
        // Lazy load Spline 3D viewer
        const splineContainer = document.getElementById('spline-container');
        if (splineContainer) {
            // Function to load Spline
            function loadSpline() {
                // Prevent duplicate loading if script already exists or viewer is already present
                if (document.querySelector('script[src*="spline-viewer.js"]') || splineContainer.querySelector('spline-viewer')) {
                    // If script exists but viewer doesn't (maybe it was removed), just create the viewer
                    if (!splineContainer.querySelector('spline-viewer')) {
                        createViewer();
                    }
                    return;
                }

                // Create script element
                const script = document.createElement('script');
                script.type = 'module';
                script.src = 'https://unpkg.com/@splinetool/viewer@1.9.37/build/spline-viewer.js';
                script.onload = createViewer;

                script.onerror = function () {
                    splineContainer.innerHTML = '<div class="error-message">Sorry, the 3D viewer could not be loaded. Please try again later.</div>';
                };

                document.body.appendChild(script);
            }

            // Internal helper to create the viewer element
            function createViewer() {
                // Check if already exists (paranoia check)
                if (splineContainer.querySelector('spline-viewer')) return;

                // CRITICAL: Ensure container is visible and has layout
                // If it's zero-height, WebGL will crash with GL_INVALID_VALUE
                const rect = splineContainer.getBoundingClientRect();
                if (rect.width === 0 || rect.height === 0) {
                    // Try again in the next frame if layout isn't ready
                    requestAnimationFrame(createViewer);
                    return;
                }

                const splineViewer = document.createElement('spline-viewer');
                splineViewer.setAttribute('url', 'https://prod.spline.design/Fn5-OJWN42hiHEhO/scene.splinecode');
                splineViewer.setAttribute('loading', 'lazy');

                splineContainer.appendChild(splineViewer);

                // Handling spinner
                const hideSpinner = () => {
                    const spinner = splineContainer.querySelector('.loading-spinner');
                    if (spinner) spinner.style.display = 'none';
                };

                splineViewer.addEventListener('load', hideSpinner);
                setTimeout(hideSpinner, 3000); // Fallback

                splineViewer.addEventListener('error', function () {
                    splineContainer.innerHTML = '<div class="error-message">Sorry, the 3D model could not be loaded.</div>';
                });
            }

            // Use IntersectionObserver to load Spline when in view
            const splineObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        // Stop observing once triggered
                        observer.unobserve(entry.target);

                        // Add a small initial delay to prioritize other assets
                        setTimeout(loadSpline, 2000);
                    }
                });
            }, {
                rootMargin: '200px 0px', // Start loading slightly before it comes into view
                threshold: 0.1
            });

            splineObserver.observe(splineContainer);
        }

        // Smooth scroll for the scroll-down arrow
        const scrollDownArrow = document.querySelector('.scroll-down-arrow');
        if (scrollDownArrow) {
            scrollDownArrow.addEventListener('click', function (e) {
                e.preventDefault();
                const targetElement = document.getElementById('home-exp');
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth' });
                }
            });
        }

        // Initialize Swiper with Autoplay (all sliders: blog + recent projects)
        document.querySelectorAll('.swiper-container').forEach(function (container) {
            new Swiper(container, {
                loop: true,  // Enable looping
                spaceBetween: 10,  // Space between slides
                slidesPerView: 1,  // Default for mobile
                observer: true,  // Enable dynamic updates
                observeParents: true,
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                pagination: {
                    el: '.swiper-pagination',
                    clickable: true,
                },
                autoplay: {
                    delay: 3000,  // Slide transition delay in milliseconds
                    disableOnInteraction: false,  // Keep autoplay running after interaction
                },
                breakpoints: {
                    576: { // For devices with a max-width of 576px
                        slidesPerView: 1,
                        spaceBetween: 10,
                    },
                    768: { // For devices with a max-width of 768px
                        slidesPerView: 2,
                        spaceBetween: 15,
                    }
                },
                on: {
                    init: function () {
                        setTimeout(() => {
                            this.update(); // Force update after initialization
                        }, 100);
                    }
                }
            });
        });

        // Reset ScrollTrigger
        ScrollTrigger.getAll().forEach(t => t.kill());
        ScrollTrigger.refresh();

        // Intro cascade: one sequenced timeline (badge -> headline -> copy ->
        // differentiators/skills -> social -> CTAs -> portrait) instead of
        // disjoint ScrollTriggers. Covers the home `.hero` and the about
        // `.about` intro alike. Built paused; played when the curtain reveal
        // completes so motion isn't wasted behind the overlay.
        // Skipped entirely under reduced-motion (content stays visible).
        if ((document.querySelector('.hero') || document.querySelector('.about .hero-content')) && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            heroIntroTl = gsap.timeline({
                paused: true,
                defaults: { ease: 'power3.out', clearProps: 'all' }
            });
            heroIntroTl
                .from('.hero .hero-content h1, .about .hero-content h2', { opacity: 0, y: 46, duration: 0.9 })
                .from('.hero .hero-content .lead, .about .hero-content p', { opacity: 0, y: 36, duration: 0.8, stagger: 0.12 }, '-=0.6')
                .from('.hero .differentiators .diff-item, .about .skills-category', { opacity: 0, y: 22, duration: 0.6, stagger: 0.1 }, '-=0.55')
                .from('.hero-social a', { opacity: 0, y: 22, duration: 0.55, stagger: 0.08 }, '-=0.5')
                .from('.hero .cta .hologram-btn, .about .buttons .cta .hologram-btn', { opacity: 0, y: 22, duration: 0.55, stagger: 0.1 }, '-=0.45')
                .from('.hero .image, .about .image', { opacity: 0, y: 40, scale: 0.97, duration: 0.9 }, '-=0.7');

            // Native loads never close the curtain, so play at once instead of
            // waiting on the (visually empty) reveal tween.
            heroIntroTl.play(0);
        }

        // Below-fold ScrollTriggers measured too early fire early (e.g. the
        // Technical Leadership cards animating before the intro). Re-measure
        // once the full page, fonts included, has settled.
        window.addEventListener('load', function () {
            ScrollTrigger.refresh();
        });
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(function () {
                ScrollTrigger.refresh();
            });
        }

        // Lazy Load/Play Videos when in Viewport
        const videoObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const video = entry.target;
                if (entry.isIntersecting) {
                    if (video.getAttribute('preload') === 'none') {
                        video.setAttribute('preload', 'metadata');
                    }
                    video.play().catch(e => {
                        console.log('Autoplay blocked:', e.message);
                    });
                } else {
                    video.pause();
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('video.background-video').forEach(video => {
            videoObserver.observe(video);
        });

        // GSAP animation for case studies
        document.querySelectorAll('.case-study').forEach(caseStudy => {
            gsap.from(caseStudy.querySelectorAll('.case-content > *, .case-content a'), {
                scrollTrigger: {
                    trigger: caseStudy,
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                y: 50,
                duration: 1,
                stagger: 0.2,
                ease: 'power2.out',
                clearProps: 'all'
            });
        });

        // GSAP animations for Services page
        if (document.querySelector('.services-section')) {
            // Animate service cards
            gsap.from('.service-card', {
                scrollTrigger: {
                    trigger: '.services-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                y: 60,
                duration: 0.8,
                stagger: 0.15,
                ease: 'power2.out',
                clearProps: 'all'
            });
        }

        if (document.querySelector('.how-i-work')) {
            // Animate work steps
            gsap.from('.how-i-work .work-step', {
                scrollTrigger: {
                    trigger: '.how-i-work',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                y: 40,
                duration: 0.7,
                stagger: 0.1,
                ease: 'power2.out',
                clearProps: 'all'
            });
        }

        if (document.querySelector('.services-cta')) {
            // Animate CTA section
            gsap.from('.services-cta h2, .services-cta .cta-buttons, .services-cta .contact-direct', {
                scrollTrigger: {
                    trigger: '.services-cta',
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                y: 30,
                duration: 0.8,
                stagger: 0.15,
                ease: 'power2.out',
                clearProps: 'all'
            });
        }

        // GSAP animations for Contact page
        if (document.querySelector('.contact-hero')) {
            gsap.from('.contact-hero .hero-content h2, .contact-hero .hero-content p', {
                scrollTrigger: {
                    trigger: '.contact-hero',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                y: 50,
                duration: 1,
                stagger: 0.2,
                ease: 'power2.out',
                clearProps: 'all'
            });
        }

        if (document.querySelector('.contact-form-section')) {
            // Animate contact info cards
            gsap.from('.contact-info-card', {
                scrollTrigger: {
                    trigger: '.contact-form-section',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                y: 40,
                duration: 0.7,
                stagger: 0.1,
                ease: 'power2.out',
                clearProps: 'all'
            });
        }

        if (document.querySelector('.what-to-expect')) {
            // Animate what to expect steps
            gsap.from('.what-to-expect .step', {
                scrollTrigger: {
                    trigger: '.what-to-expect',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                x: -30,
                duration: 0.6,
                stagger: 0.15,
                ease: 'power2.out',
                clearProps: 'all'
            });
        }

        // GSAP animations for About page - Technical Leadership
        if (document.querySelector('.technical-leadership')) {
            // Animate section header
            gsap.from('.technical-leadership .hero-content h2, .technical-leadership .hero-content p', {
                scrollTrigger: {
                    trigger: '.technical-leadership',
                    start: 'top 80%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                y: 40,
                duration: 0.8,
                stagger: 0.15,
                ease: 'power2.out',
                clearProps: 'all'
            });

            // Animate leadership cards
            gsap.from('.technical-leadership .work-step', {
                scrollTrigger: {
                    trigger: '.technical-leadership .row',
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                    once: true
                },
                opacity: 0,
                y: 50,
                duration: 0.7,
                stagger: 0.12,
                ease: 'power2.out',
                clearProps: 'all'
            });
        }
    }

    // Wait for DOM to be fully loaded for initial run
    document.addEventListener('DOMContentLoaded', function () {
        // Pre-init settings
        gsap.config({ nullTargetWarn: false });

        // Navbar scroll behavior (needs to stay outside initPage as it's global)
        let lastScroll = 0;
        const navbar = document.getElementById('wrapper-navbar');
        const delta = 5;
        const navbarHeight = navbar?.offsetHeight || 0;

        window.addEventListener('scroll', function () {
            if (!navbar) return;
            const currentScroll = window.pageYOffset;
            if (Math.abs(lastScroll - currentScroll) <= delta) return;

            if (currentScroll > lastScroll && currentScroll > navbarHeight) {
                navbar.classList.add('nav-up');
            } else {
                if (currentScroll + window.innerHeight < document.documentElement.scrollHeight) {
                    navbar.classList.remove('nav-up');
                }
            }
            lastScroll = currentScroll;
        }, { passive: true });

        // Initial Page Load (native navigation = fresh JS state every load,
        // so initPage only ever runs once — no stale Swipers/observers/ScrollTriggers).
        initPage();

        /**
         * GSAP-only page transitions (no Barba): same curtain choreography,
         * native browser navigation. Lighter bundle, zero PJAX state leaks,
         * forms/anchors/auth all behave natively.
         */
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
            // Accessibility: no curtain choreography, content is simply there.
            gsap.set('.transition-curtain .strip', { scaleY: 0 });
        } else {
            // Reveal: lift the curtain on every (native) page load.
            // Gentle asymmetric exit — slow sine cascade reads as natural,
            // where the punchier expo cover reads as intentional.
            gsap.to('.transition-curtain .strip', {
                scaleY: 0,
                duration: 1.0,
                stagger: 0.08,
                ease: 'sine.inOut',
                transformOrigin: 'bottom'
            });

            // Intercept internal links: close the curtain, then navigate natively.
            document.addEventListener('click', function (e) {
                if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

                const link = e.target.closest ? e.target.closest('a[href]') : null;
                if (!link) return;

                const href = link.getAttribute('href');
                if (!href || href.charAt(0) === '#') return;
                if (link.target === '_blank' || link.hasAttribute('download')) return;

                let url;
                try {
                    url = new URL(href, window.location.origin);
                } catch (err) {
                    return;
                }

                // External links: leave alone.
                if (url.origin !== window.location.origin) return;
                // Same-page anchors: leave alone (smooth-scroll handlers own these).
                if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
                // WordPress system routes + API + files: always native, no curtain.
                if (/^\/wp-(admin|login|cron)/.test(url.pathname) || url.pathname.indexOf('wp-json') !== -1) return;
                if (/\.(pdf|zip|rar|csv|xlsx?|docx?|mp4|webm)$/i.test(url.pathname)) return;

                e.preventDefault();

                let navigated = false;
                const go = function () {
                    if (navigated) return;
                    navigated = true;
                    window.location.href = url.href;
                };

                try {
                    gsap.timeline({ onComplete: go }).to('.transition-curtain .strip', {
                        scaleY: 1,
                        duration: 0.8,
                        stagger: 0.06,
                        ease: 'expo.inOut',
                        transformOrigin: 'top'
                    });
                    setTimeout(go, 1500); // Failsafe: never trap the user behind the curtain.
                } catch (err) {
                    go();
                }
            });
        }
    });
})();
